/**
 * Pilot Cohort Admin Page — dedicated kanban for Founding Pilot applicants.
 *
 * Different from AdminPilotsPage (which manages Pilot-User admin access) —
 * this page tracks Founding Pilot Program applicants who came in via the
 * `/contact?intent=pilot` funnel. They live in `contact_requests` with
 * `intent="founding_pilot"` and their own status pipeline.
 *
 * Feeds off:
 *   GET   /api/admin/pilots                    — inbox with per-status stats
 *   PATCH /api/admin/pilot-cohort/{id}               — inline status/notes edit
 *   GET   /api/admin/pilot-cohort/reply-templates    — canned replies for copy-to-clipboard
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Sparkle, ClipboardText, ArrowRight, Phone, EnvelopeSimple,
  ChatCircleDots,
} from "@phosphor-icons/react";
import client from "../lib/api";

const STATUS_META = {
  pilot_new:        { label: "New",         color: "border-[#FF5A1F]/40 bg-[#FF5A1F]/[0.06] text-[#FF9764]" },
  pilot_scheduled:  { label: "Scheduled",   color: "border-amber-400/40 bg-amber-400/[0.06] text-amber-300" },
  pilot_onboarded:  { label: "Onboarded",   color: "border-emerald-400/40 bg-emerald-400/[0.06] text-emerald-300" },
  pilot_declined:   { label: "Declined",    color: "border-neutral-600 bg-neutral-800/50 text-neutral-500" },
};

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function renderTemplate(body, applicant) {
  const first = (applicant.contact_name || "").split(" ")[0] || "there";
  return body
    .replaceAll("{first_name}", first)
    .replaceAll("{company_name}", applicant.company_name || "your team");
}

export default function AdminPilotCohortPage() {
  const [data, setData] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");
  const [openId, setOpenId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [inbox, tpl] = await Promise.all([
        client.get("/admin/pilot-cohort"),
        client.get("/admin/pilot-cohort/reply-templates"),
      ]);
      setData(inbox.data);
      setTemplates(tpl.data?.templates || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load pilot inbox");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const updateStatus = async (cid, status) => {
    try {
      await client.patch(`/admin/pilot-cohort/${cid}`, { status });
      toast.success(`Moved to ${STATUS_META[status]?.label || status}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const saveNotes = async (cid, admin_notes) => {
    try {
      await client.patch(`/admin/pilot-cohort/${cid}`, { admin_notes });
      toast.success("Notes saved");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const filtered = useMemo(() => {
    if (!data) return [];
    if (tab === "all") return data.applicants;
    return data.applicants.filter((a) => a.status === tab);
  }, [data, tab]);

  return (
    <div className="p-6 sm:p-8 space-y-6" data-testid="admin-pilot-cohort-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#FF9764] font-semibold mb-1 flex items-center gap-1.5">
            <Sparkle size={11} weight="fill" /> Founding Pilot Cohort
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">
            Pilot inbox
          </h1>
          <p className="text-sm text-neutral-500 mt-1">
            Every applicant from{" "}
            <span className="text-neutral-300">/contact?intent=pilot</span> lands here — pipe them through the pipeline.
          </p>
        </div>
        <button
          onClick={load}
          data-testid="pilots-refresh"
          className="text-xs uppercase tracking-widest text-neutral-500 hover:text-white border border-white/10 hover:border-white/30 rounded-full px-3 py-1.5 transition-colors"
        >
          Refresh
        </button>
      </div>

      <CohortCloseDateSetter />

      <div className="grid gap-3 grid-cols-2 sm:grid-cols-5" data-testid="pilots-stats">
        <StatCell label="All" value={data?.stats?.all || 0} active={tab === "all"} onClick={() => setTab("all")} testid="stat-all" />
        {Object.keys(STATUS_META).map((s) => (
          <StatCell
            key={s}
            label={STATUS_META[s].label}
            value={data?.stats?.[s] || 0}
            active={tab === s}
            onClick={() => setTab(s)}
            testid={`stat-${s}`}
            accent={STATUS_META[s].color}
          />
        ))}
      </div>

      {loading ? (
        <div className="text-sm text-neutral-500 py-16 text-center">Loading pilot applicants…</div>
      ) : filtered.length === 0 ? (
        <div
          data-testid="pilots-empty-state"
          className="rounded-xl border border-dashed border-white/10 py-16 text-center"
        >
          <div className="text-sm text-neutral-400 max-w-md mx-auto">
            {tab === "all"
              ? "No pilot applications yet. Share freightpilotai.ai/contact?intent=pilot in your cold outreach and warm channels — they'll land here."
              : `Nobody in ${STATUS_META[tab]?.label || tab} yet.`}
          </div>
        </div>
      ) : (
        <div className="space-y-2" data-testid="pilots-list">
          {filtered.map((a) => (
            <ApplicantRow
              key={a.id}
              applicant={a}
              open={openId === a.id}
              onToggle={() => setOpenId(openId === a.id ? null : a.id)}
              onStatus={(s) => updateStatus(a.id, s)}
              onSaveNotes={(notes) => saveNotes(a.id, notes)}
              templates={templates}
            />
          ))}
        </div>
      )}
    </div>
  );
}

const StatCell = ({ label, value, active, onClick, testid, accent }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`text-left rounded-xl border p-3.5 transition-colors ${
      active
        ? "border-white/30 bg-white/[0.04]"
        : "border-white/10 bg-white/[0.02] hover:border-white/20"
    }`}
  >
    <div className={`text-[10px] uppercase tracking-widest font-semibold mb-1.5 ${accent ? accent.split(" ")[2] : "text-neutral-500"}`}>
      {label}
    </div>
    <div className="text-2xl font-semibold tabular-nums">{value}</div>
  </button>
);

// Wave 16 · Founder-controlled cohort close date.
// Writes to /admin/pilot-cohort/settings, which the public
// /founding-pilots/count endpoint reads with highest priority. Setting
// this replaces the rolling 14-day default with a real deadline visitors
// can feel. Empty string clears the override.
function CohortCloseDateSetter() {
  const [settings, setSettings] = useState(null);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await client.get("/admin/pilot-cohort/settings");
      setSettings(data);
      // Convert the effective ISO to the datetime-local format the input needs
      const iso = data.cohort_close_iso;
      if (iso) {
        const d = new Date(iso);
        // datetime-local expects "YYYY-MM-DDTHH:mm"
        const pad = (n) => String(n).padStart(2, "0");
        setDraft(
          `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
        );
      } else {
        setDraft("");
      }
    } catch (e) {
      // Silent — page still usable without this widget
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      // Convert datetime-local back to ISO with timezone
      const iso = draft ? new Date(draft).toISOString() : "";
      await client.post("/admin/pilot-cohort/settings", { cohort_close_iso: iso });
      toast.success(iso ? "Close date saved" : "Close date cleared — using rolling default");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const clearDate = async () => {
    setDraft("");
    setSaving(true);
    try {
      await client.post("/admin/pilot-cohort/settings", { cohort_close_iso: "" });
      toast.success("Cleared — rolling 14-day default active");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Clear failed");
    } finally {
      setSaving(false);
    }
  };

  const effective = settings?.effective_close_iso;
  const isOverridden = !!settings?.cohort_close_iso;

  return (
    <div
      data-testid="cohort-close-date-setter"
      className="rounded-xl border border-[#FF5A1F]/20 bg-[#FF5A1F]/[0.02] p-4"
    >
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-[#FF9764] font-semibold mb-1">
            Cohort Close Date
          </div>
          <div className="text-sm text-white leading-snug">
            Sets the "Cohort 1 closes in X days" countdown visitors see on the hero.
          </div>
          <div className="text-[11px] text-neutral-500 mt-1" data-testid="cohort-close-effective">
            Currently showing: <span className="text-neutral-300">{effective ? new Date(effective).toLocaleString() : "—"}</span>
            {!isOverridden && <span className="text-neutral-600 ml-2">(rolling 14-day default)</span>}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input
            type="datetime-local"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            data-testid="cohort-close-date-input"
            className="rounded-lg bg-white/[0.04] border border-white/10 focus:border-[#FF5A1F] focus:outline-none px-3 py-2 text-sm text-white [color-scheme:dark]"
          />
          <button
            onClick={save}
            disabled={saving || !draft}
            data-testid="cohort-close-date-save"
            className="rounded-full px-4 py-2 text-xs font-medium bg-[#FF5A1F] hover:bg-[#FF7038] text-white disabled:opacity-60 transition-colors"
          >
            {saving ? "..." : "Save"}
          </button>
          {isOverridden && (
            <button
              onClick={clearDate}
              disabled={saving}
              data-testid="cohort-close-date-clear"
              className="rounded-full px-3 py-2 text-xs border border-white/15 hover:border-white/40 text-neutral-300 hover:text-white transition-colors"
            >
              Reset
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function ApplicantRow({ applicant: a, open, onToggle, onStatus, onSaveNotes, templates }) {
  const [notes, setNotes] = useState(a.admin_notes || "");
  const meta = STATUS_META[a.status] || STATUS_META.pilot_new;
  const copyTemplate = (body) => {
    try {
      navigator.clipboard.writeText(renderTemplate(body, a));
      toast.success("Copied to clipboard — paste into your reply");
    } catch {
      toast.error("Copy failed");
    }
  };
  return (
    <div
      className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden"
      data-testid={`pilot-row-${a.id}`}
    >
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-4 p-4 text-left hover:bg-white/[0.02] transition-colors"
      >
        <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-semibold border ${meta.color} shrink-0`}>
          {meta.label}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm text-white font-medium truncate">
            {a.contact_name}
            <span className="text-neutral-500 font-normal"> · {a.company_name}</span>
          </div>
          <div className="text-[11px] text-neutral-500 truncate">
            {a.email} · {a.phone || "no phone"} · fleet: {a.fleet_size || "—"}
          </div>
        </div>
        <div className="text-[11px] text-neutral-500 tabular-nums shrink-0">
          {fmtDate(a.created_at)}
        </div>
      </button>
      {open && (
        <div className="border-t border-white/5 p-5 grid gap-4 md:grid-cols-2" data-testid={`pilot-detail-${a.id}`}>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-2">
              What they said
            </div>
            <div className="rounded-md bg-black/40 border border-white/5 p-3 text-sm text-neutral-200 leading-relaxed whitespace-pre-line min-h-[80px]">
              {a.current_challenges || <span className="text-neutral-600 italic">— no notes —</span>}
            </div>
            <div className="mt-3 flex flex-wrap gap-2 text-[11px] text-neutral-400">
              {a.services_of_interest && (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white/5">
                  <ChatCircleDots size={11} /> {a.services_of_interest}
                </span>
              )}
              {a.preferred_time && (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white/5">
                  ⏰ {a.preferred_time}
                </span>
              )}
            </div>
          </div>
          <div className="space-y-4">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-2">
                Move to status
              </div>
              <div className="flex flex-wrap gap-1.5">
                {Object.keys(STATUS_META).map((s) => (
                  <button
                    key={s}
                    onClick={() => onStatus(s)}
                    disabled={a.status === s}
                    data-testid={`pilot-status-${s}-${a.id}`}
                    className={`text-[11px] px-2.5 py-1.5 rounded-full border transition-colors ${
                      a.status === s
                        ? `${STATUS_META[s].color} cursor-default`
                        : "border-white/15 text-neutral-300 hover:border-white/40 hover:bg-white/5"
                    }`}
                  >
                    {STATUS_META[s].label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-2 flex items-center gap-1">
                <ClipboardText size={11} weight="bold" /> Quick reply templates
              </div>
              <div className="grid grid-cols-2 gap-1.5">
                {templates.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => copyTemplate(t.body)}
                    data-testid={`pilot-template-${t.id}`}
                    className="text-[11px] text-left px-2.5 py-2 rounded-md border border-white/10 hover:border-[#FF5A1F]/40 hover:bg-[#FF5A1F]/5 transition-colors"
                    title="Copy to clipboard"
                  >
                    {t.label}
                    <ArrowRight size={9} weight="bold" className="inline ml-1 text-neutral-500" />
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-2">
                Private notes
              </div>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                onBlur={() => notes !== (a.admin_notes || "") && onSaveNotes(notes)}
                placeholder="Auto-saves on blur"
                rows={3}
                data-testid={`pilot-notes-${a.id}`}
                className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm focus:border-[#FF5A1F]/50 focus:outline-none placeholder:text-neutral-600 resize-none"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <a
                href={`mailto:${a.email}?subject=${encodeURIComponent(`FreightPilot AI Pilot Program — ${a.company_name}`)}`}
                className="text-[11px] inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full border border-white/15 hover:border-white/40 hover:bg-white/5 transition-colors"
              >
                <EnvelopeSimple size={11} /> Email
              </a>
              {a.phone && (
                <a
                  href={`tel:${a.phone}`}
                  className="text-[11px] inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full border border-white/15 hover:border-white/40 hover:bg-white/5 transition-colors"
                >
                  <Phone size={11} /> Call
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
